<template>
  <div>
    <div class="d-flex justify-content-between align-items-baseline mb-2">
      <h6 class="mb-0 font-weight-bold">
        Sections <span class="badge badge-light border ml-1">{{ sections.length }}</span>
      </h6>
      <small class="text-muted">Each section is one heading in the picker, with its slide types.</small>
    </div>

    <div v-for="(section, sIndex) in sections" :key="sIndex" class="card mb-3 border">
      <div class="card-header section-header d-flex justify-content-between align-items-center py-2">
        <div>
          <span class="badge badge-pill badge-primary mr-1">{{ sIndex + 1 }}</span>
          <span class="font-weight-bold">Section {{ sIndex + 1 }}</span>
          <span v-if="section.title" class="text-muted ml-1">· {{ section.title }}</span>
        </div>
        <b-button
          size="sm"
          variant="link"
          class="text-danger p-0"
          @click="removeSection(sIndex)"
        >
          <i class="fa fa-trash"></i> Remove section
        </b-button>
      </div>
      <div class="card-body p-3">
        <div class="form-row mb-3">
          <div class="col-md-4 mb-2 mb-md-0">
            <label class="small text-muted mb-1">
              Section id <span class="text-danger">*</span>
              <i v-b-tooltip.hover class="fa fa-question-circle ml-1" title="A short, stable id, for example quizzes."></i>
            </label>
            <b-form-input v-model.trim="section.id" size="sm" placeholder="quizzes" :state="section.id ? null : false" />
          </div>
          <div class="col-md-4 mb-2 mb-md-0">
            <label class="small text-muted mb-1">
              Title
              <i v-b-tooltip.hover class="fa fa-question-circle ml-1" title="Optional heading shown above the section in the picker."></i>
            </label>
            <b-form-input v-model="section.title" size="sm" placeholder="Quizzes" />
          </div>
          <div class="col-md-4">
            <label class="small text-muted mb-1">
              Source <span class="text-danger">*</span>
              <i
                v-b-tooltip.hover
                class="fa fa-question-circle ml-1"
                title="static renders the slide types listed below. catalogue is reserved for future dynamic sections and renders nothing today."
              ></i>
            </label>
            <b-form-select
              v-model="section.source"
              size="sm"
              :options="sourceOptions(section.source)"
              :state="section.source ? null : false"
            />
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-baseline border-top pt-2 mb-2">
          <span class="small font-weight-bold text-uppercase text-muted">
            Slide types ({{ section.items.length }})
          </span>
        </div>

        <div v-if="slideTypeCatalogueUsable" class="d-flex align-items-start mb-2">
          <multiselect
            :value="bulkSelections[sIndex] || []"
            :options="availableSlideTypes(section)"
            :custom-label="slideTypeLabel"
            :multiple="true"
            :close-on-select="false"
            placeholder="Search slide types to add several at once"
            :show-labels="false"
            open-direction="bottom"
            class="flex-grow-1 mr-2"
            @input="(val) => $set(bulkSelections, sIndex, val)"
          />
          <b-button
            size="sm"
            variant="primary"
            class="text-nowrap mt-1"
            :disabled="!(bulkSelections[sIndex] || []).length"
            @click="addBulkItems(section, sIndex)"
          >
            <i class="fa fa-plus"></i> Add{{ (bulkSelections[sIndex] || []).length ? ' ' + bulkSelections[sIndex].length : '' }}
          </b-button>
        </div>

        <table v-if="section.items.length" class="table table-sm mb-2">
          <thead>
            <tr>
              <th class="border-top-0 small text-muted" style="width:70%">Slide type</th>
              <th class="border-top-0 small text-muted text-center">
                Featured
                <i v-b-tooltip.hover class="fa fa-question-circle ml-1" title="Pins this slide type to the top of the section in the picker."></i>
              </th>
              <th class="border-top-0"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, iIndex) in section.items" :key="iIndex">
              <td>
                <multiselect
                  v-if="slideTypeCatalogueUsable"
                  v-model="item.key"
                  :options="slideTypeCatalogue"
                  :custom-label="slideTypeLabel"
                  placeholder="Search a slide type"
                  :allow-empty="false"
                  :show-labels="false"
                  open-direction="bottom"
                />
                <b-form-input
                  v-else
                  v-model.trim="item.key"
                  size="sm"
                  placeholder="quiz/multipleChoice"
                  :state="item.key ? (isDuplicate(item.key) ? false : null) : false"
                />
                <small
                  v-if="slideTypeCatalogueUsable && item.key && slideTypeCatalogue.indexOf(item.key) === -1"
                  class="text-danger"
                >
                  not in catalogue
                </small>
                <small v-if="item.key && isDuplicate(item.key)" class="text-danger">
                  duplicate key
                </small>
                <small v-if="!slideTypeCatalogueUsable && item.key && slideTypeNames[item.key]" class="d-block text-muted">
                  {{ slideTypeNames[item.key] }}
                </small>
              </td>
              <td class="align-middle text-center"><b-form-checkbox v-model="item.featured" class="ml-2" /></td>
              <td class="align-middle text-right">
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="link"
                  class="text-muted p-0"
                  title="Remove slide type"
                  @click="removeItem(section, iIndex)"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
        <p v-else class="small text-muted mb-2">No slide types yet.</p>

        <b-button size="sm" variant="link" class="p-0" @click="addItem(section)">
          <i class="fa fa-plus"></i> Add one slide type
        </b-button>
      </div>
    </div>

    <b-button variant="outline-primary" block class="border-dashed" @click="addSection">
      <i class="fa fa-plus"></i> Add section
    </b-button>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect"

export default {
  name: "SectionsEditor",
  components: { Multiselect },
  props: {
    sections: { type: Array, required: true },
    duplicateKeys: { type: [Object, Set], required: true },
    slideTypeCatalogue: { type: Array, default: () => [] },
    // false when the catalogue fetch failed or returned nothing, so callers fall
    // back to free text instead of a dead dropdown that blocks authoring.
    slideTypeCatalogueUsable: { type: Boolean, default: false },
    slideTypeNames: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      bulkSelections: {},
    }
  },
  methods: {
    availableSlideTypes(section) {
      return this.slideTypeCatalogue.filter((key) => !section.items.some((item) => item.key === key))
    },
    addBulkItems(section, sIndex) {
      const keys = this.bulkSelections[sIndex] || []
      const filledItems = section.items.filter((item) => item.key)
      section.items.splice(0, section.items.length, ...filledItems)
      keys.forEach((key) => {
        if (!section.items.some((item) => item.key === key)) {
          section.items.push({ key, featured: false })
        }
      })
      this.$set(this.bulkSelections, sIndex, [])
    },
    slideTypeLabel(key) {
      const name = this.slideTypeNames[key]
      return name ? name + " (" + key + ")" : key
    },
    isDuplicate(key) {
      return Boolean(this.duplicateKeys && typeof this.duplicateKeys.has === "function" && this.duplicateKeys.has(key))
    },
    sourceOptions(current) {
      const options = [
        { value: "static", text: "static" },
        { value: "catalogue", text: "catalogue (reserved)" },
      ]
      if (current && current !== "static" && current !== "catalogue") {
        options.push({ value: current, text: current + " (custom)" })
      }
      return options
    },
    addSection() {
      this.sections.push({ id: "", title: "", source: "static", items: [{ key: "", featured: false }] })
    },
    removeSection(index) {
      this.sections.splice(index, 1)
      this.bulkSelections = {}
    },
    addItem(section) {
      section.items.push({ key: "", featured: false })
    },
    removeItem(section, index) {
      section.items.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.border-dashed {
  border-style: dashed;
}
.section-header {
  background-color: #f1f5fb;
}
</style>
